/**
 * travelAllowance — Pauschalen-Berechnungskern der Dienstreisen-/Reisekostenerfassung (RK-2).
 *
 * `calculateTravelAllowances` ist eine TOTALE, deterministische, seiteneffektfreie Funktion
 * (Muster `buildDatevExport`): sie wirft NIE, sondern liefert jeden Fehler als
 * `{ ok: false, error }`-Union. Keine Firebase-/React-Imports — voll unit-testbar.
 *
 * Fachliche Grundlage: architecture-reisekosten.md §3.2 (Berechnungsregeln 1–9 + Edge-Case-
 * Katalog) und §11 (kanonische Soll-Paare #1–#27, verbindlicher Test-Contract). Rechtsnorm:
 * §9 Abs.4a EStG (Verpflegungsmehraufwand). Alle Beträge in Integer-Cents — nie Floats für Geld.
 *
 * Leitprinzipien:
 *   - Layer-1 fail-closed IM Kern (Regel 8): jeder numerische Eingangswert + jedes Ergebnisfeld
 *     wird SELBST auf `Number.isFinite`/`Number.isInteger` geprüft — nie auf `travelValidation`
 *     verlassen. Ein `NaN` erreicht strukturell nie einen Snapshot.
 *   - Deterministische Fehler-Prüfreihenfolge (Regel 9): erste Verletzung gewinnt, es wird
 *     IMMER genau EIN Fehlercode geliefert.
 *   - Übernachtungs-Gate (Regel 1, §9 Abs.4a S.3 Nr.2 EStG): NIE 14/28 € ohne lückenlose
 *     Übernachtungskette (fail-closed `missingOvernightForMultiDay`).
 *
 * @module travelAllowance
 */

import type { TravelRatesConfig, TravelForeignRatesConfig } from './travelRates';
import {
  resolvePerDiemRate,
  resolveMealReductions,
  resolveMileageRate,
  resolveForeignPerDiemRate,
  isForeignDateAssured,
} from './travelRatesResolver';
import type { ForeignRateSource } from './travelRatesResolver';
import type { TravelDayRecord, TravelMileage } from './travelCalcTypes';

// Eingabe-Datentypen bewusst re-exportiert, damit Konsumenten alles aus travelAllowance ziehen
// können (RK-3 re-exportiert TravelDayRecord/TravelMileage aus travelCalcTypes — dort SoT).
export type { TravelDayRecord, TravelMileage } from './travelCalcTypes';

// ─── Typen (Vertrag §3.2) ─────────────────────────────────────────────────────────

/** Tagestyp einer Reise. Steuert die Basis-Pauschale (Regel 2). */
export type TravelDayType = 'eintaegig' | 'anreise' | 'zwischentag' | 'abreise';

/** Eingabe des Rechenkerns. `days` MUSS die Kalendertage departure..return lückenlos abdecken. */
export interface TravelCalcInput {
  /** Abreisedatum 'YYYY-MM-DD'. */
  departureDateISO: string;
  /** Abreisezeit 'HH:mm' (24h). */
  departureTime: string;
  /** Rückkehrdatum 'YYYY-MM-DD'. */
  returnDateISO: string;
  /** Rückkehrzeit 'HH:mm' (24h). */
  returnTime: string;
  /** Ein Eintrag je Kalendertag (§4.1) — lückenlos, jeder Kalendertag genau einmal. */
  days: TravelDayRecord[];
  /** Kilometergeld-Angabe oder `null` (keine eigene Fahrt). */
  mileage: TravelMileage | null;
  /** Erste Tätigkeitsstätte → `firstWorkplaceTrip` (Pendlerpauschale ≠ Reisekosten, §6.2). */
  isFirstWorkplace: boolean;
}

/**
 * Fehlercodes. Genau EIN Code je fehlgeschlagenem Aufruf, in der Prüfreihenfolge aus Regel 9:
 * invalidDateRange → daysMismatch → nonFiniteInput → firstWorkplaceTrip →
 * missingOvernightForMultiDay → foreignCountryNotSupported →
 * foreignRatesNotYetAvailableForDate → noRatesForDate.
 */
export type TravelCalcErrorCode =
  | 'invalidDateRange'
  | 'daysMismatch'
  | 'nonFiniteInput'
  | 'firstWorkplaceTrip'
  | 'missingOvernightForMultiDay'
  | 'foreignCountryNotSupported'
  /**
   * NEU (A-6, architecture-travel-rates-validity.md §5): ein Auslandstag der Reise liegt NACH dem
   * internen Zusicherungs-Horizont der Auslands-Sätze (`assuredUntilISO`). Fail-closed nach OBEN —
   * bewusst ein EIGENER, von `noRatesForDate` unterscheidbarer Code (dort fehlt der Satz ganz,
   * hier ist der Datenbestand für den Zeitraum nur noch nicht freigegeben).
   *
   * All-or-nothing: EIN betroffener Auslandstag sperrt die GESAMTE Reise, es entsteht keine
   * Teilsumme aus deren Inlandstagen (V8, Grundlage des Kundentexts A-7).
   */
  | 'foreignRatesNotYetAvailableForDate'
  | 'noRatesForDate';

/** Warnungen (informativ, kein Fehler). `threeMonthRuleNotChecked` wird bei ok IMMER emittiert. */
export type TravelCalcWarningCode =
  | 'shortSingleDayNoAllowance'
  | 'midnightSingleDaySplit'
  | 'threeMonthRuleNotChecked';

/** Ergebnis eines einzelnen Reisetages (Integer-Cents). */
export interface TravelDayCalcResult {
  dateISO: string;
  dayType: TravelDayType;
  countryCode: string;
  /**
   * Basis-Pauschale vor Kürzung. Inland: 2800 | 1400 | 0. Ausland (§13.11 Regel 11):
   * länderindividueller `fullDayCents` (Zwischentag) bzw. `arrivalDepartureDayCents`
   * (An-/Abreise, eintägig >8h) des aufgelösten Länder-/Regions-Satzes — KEIN 80%-Abschlag.
   */
  baseCents: number;
  /** Frühstückskürzung — IMMER vom vollen Tagessatz DES TAGES-LANDES (Inland 560; Ausland 20 %). */
  reductionBreakfastCents: number;
  /** Mittagskürzung (Inland 1120; Ausland 40 % vom Länder-vollen-Tagessatz / 0). */
  reductionLunchCents: number;
  /** Abendkürzung (Inland 1120; Ausland 40 % vom Länder-vollen-Tagessatz / 0). */
  reductionDinnerCents: number;
  /** `max(0, base − Σ Kürzungen)` — Deckelung PRO TAG, nie negativ. */
  allowanceCents: number;
  /**
   * NEU (RK-14, §13.5/§13.9): Herkunft des angewandten Auslands-Satzes (Auflösungsstufe der
   * Kaskade `resolveForeignPerDiemRate`). NUR auf non-DE-Tagen gesetzt, die über
   * `options.foreignRates` aufgelöst wurden — DE-Tage (auch in Mischreisen) tragen den Key NIE
   * (LANDMINE: Key WEGLASSEN statt `undefined` — hält toEqual-Fixtures #1–#27 grün + verhindert
   * die Firestore-undefined-Write-Crash-Klasse V10 im Settle-Snapshot).
   */
  rateSource?: ForeignRateSource;
}

/** Ergebnis-Union (total function). */
export type TravelCalcResult =
  | {
      ok: true;
      days: TravelDayCalcResult[];
      /** Σ days.allowanceCents. */
      verpflegungCents: number;
      /** `round(km × Satz)`; 0 wenn mileage null. */
      mileageCents: number;
      /** verpflegung + mileage (NUR Pauschalen — Ist-Kosten NICHT enthalten). */
      totalCents: number;
      warnings: TravelCalcWarningCode[];
      ratesVersionId: string;
      /**
       * NEU (RK-14, §13.5/§13.9): `versionId` des angewandten Auslands-Jahrgangs
       * (`options.foreignRates.versionId`). NUR gesetzt, wenn ≥1 Tag über `foreignRates`
       * aufgelöst wurde (mind. ein Tag trägt `rateSource`). Reine Inlandsreisen (auch mit
       * übergebenem `foreignRates`, Soll-Paar #39) lassen den Key WEG — nie `undefined`
       * materialisieren (toEqual-Fixtures #1–#27 + `assertNoUndefinedValues`, V10).
       */
      foreignRatesVersionId?: string;
    }
  | { ok: false; error: TravelCalcErrorCode };

/**
 * NEU (RK-14, §13.5): additiv-optionaler 3. Parameter von `calculateTravelAllowances`.
 * Weggelassen/`undefined` ⇒ Verhalten byte-identisch zum Inlands-MVP (non-DE ⇒
 * `foreignCountryNotSupported`, Soll-Paar #40). Nur übergeben, wenn das Sub-Flag-Gate
 * (`getForeignRatesForUser`, §13.7) offen ist — fail-closed.
 */
export interface TravelCalcOptions {
  /** Versionierte Auslands-Sätze (generiertes Asset `TRAVEL_RATES_FOREIGN`, RK-13). */
  foreignRates?: TravelForeignRatesConfig;
}

// ─── Konstanten / kleine Helfer ─────────────────────────────────────────────────

const MINUTES_PER_DAY = 1440;
const MS_PER_DAY = 86_400_000;
const EIGHT_HOURS_MINUTES = 480; // §9 Abs.4a S.3 Nr.3: „mehr als 8 Stunden" ⇒ Schwelle strikt > 480.

/** Kurzschluss-Helfer für den Fehler-Zweig der Union. */
function fail(error: TravelCalcErrorCode): TravelCalcResult {
  return { ok: false, error };
}

/**
 * Parst ein 'YYYY-MM-DD' und liefert die Tagesnummer seit Epoch (ganzzahlig, UTC-Mitternacht)
 * oder `null` bei ungültigem Format/Kalenderdatum (z.B. '2026-02-30'). Reiner UTC-Vergleich
 * ⇒ keine Zeitzonen-Fallstricke.
 */
function parseIsoDateToDayNumber(dateISO: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) {
    return null;
  }
  const year = Number(dateISO.slice(0, 4));
  const month = Number(dateISO.slice(5, 7));
  const day = Number(dateISO.slice(8, 10));
  const utcMs = Date.UTC(year, month - 1, day);
  if (!Number.isFinite(utcMs)) {
    return null;
  }
  const roundTrip = new Date(utcMs);
  // Verwirft überrollte Daten wie '2026-02-30' (Date.UTC normalisiert sie auf März).
  if (
    roundTrip.getUTCFullYear() !== year ||
    roundTrip.getUTCMonth() !== month - 1 ||
    roundTrip.getUTCDate() !== day
  ) {
    return null;
  }
  return Math.round(utcMs / MS_PER_DAY);
}

/** Tagesnummer → 'YYYY-MM-DD' (für die Erwartungs-Datumsliste im daysMismatch-Check). */
function dayNumberToIso(dayNumber: number): string {
  return new Date(dayNumber * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * Parst 'HH:mm' (00:00–23:59) und liefert Minuten seit Mitternacht oder `null`.
 * '24:00', '24:99' u.ä. sind ungültig (§3.2: unparsebare Zeitangabe ⇒ invalidDateRange).
 */
function parseHhMmToMinutes(time: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  if (match === null) {
    return null;
  }
  return Number(match[1]) * 60 + Number(match[2]);
}

/** Kürzungsbetrag einer Mahlzeit: `round(fullDayCents × pct / 100)`, kaufmännisch (Regel 3/6). */
function mealReductionCents(fullDayCents: number, pct: number, provided: boolean): number {
  if (!provided) {
    return 0;
  }
  return Math.round((fullDayCents * pct) / 100);
}

// ─── Berechnungskern ──────────────────────────────────────────────────────────────

/**
 * DER Pauschalen-Rechner (§3.2). Berechnet Verpflegungspauschale (inkl. Mahlzeitenkürzung,
 * Übernachtungs-Gate, Mitternachts-Sonderfall) und Kilometergeld für eine Reise. Wirft NIE.
 *
 * Prüfreihenfolge (Regel 9, erste Verletzung gewinnt):
 * 1. `invalidDateRange`  — unparsebares Datum/Zeit oder Rückkehr < Abreise.
 * 2. `daysMismatch`      — days[] deckt die Kalendertage nicht lückenlos/eindeutig ab.
 * 3. `nonFiniteInput`    — km nicht `Number.isFinite` (Eingangswert; Konfig-/Ergebnis-Guard folgt).
 * 4. `firstWorkplaceTrip`— erste Tätigkeitsstätte (Pendlerpauschale-Fall).
 * 5. `missingOvernightForMultiDay` — mehrtägig ohne lückenlose Übernachtungskette.
 * 6. `foreignCountryNotSupported`  — erster Tag mit countryCode !== 'DE' (chronologisch).
 *    ⚠️ ENTFÄLLT, wenn `options.foreignRates` übergeben ist (Sub-Flag offen, §13.5 Regel 10):
 *    non-DE-Tage werden dann in Schritt 7 über `resolveForeignPerDiemRate` aufgelöst.
 * 7. Satz-Auflösung je Tag, chronologisch — zwei Stufen (A-6, §5):
 *    7a. `foreignRatesNotYetAvailableForDate` — non-DE-Tag mit übergebenem `foreignRates`, dessen
 *        Datum NACH dem Zusicherungs-Horizont liegt (`!isForeignDateAssured`). Wird VOR dem
 *        Resolver-Aufruf geprüft, damit die Codes trennscharf bleiben: der Kern sieht nie ein
 *        Resolver-`null`, das nur am Horizont liegt. DE-Tage prüfen den Horizont NICHT
 *        (eigener Inlands-Datensatz, dessen Sätze im Gesetz selbst stehen).
 *    7b. `noRatesForDate` — Resolver liefert null für ersten betroffenen Tag (chronologisch);
 *        für non-DE-Tage die Auslands-Kaskade (Land→Region→Luxemburg), für DE-Tage die
 *        Inland-Config.
 * Abschluss-Guard (Ergebnis-Ebene, Regel 8) läuft zuletzt — bei validen Inputs unerreichbar.
 *
 * @param input   Reisedaten (Zeiten, Tage, Kilometer, erste-Tätigkeitsstätte-Flag).
 * @param cfg     Versionierte INLANDS-Sätze-Konfiguration (RK-1, SoT). Auch DE-Tage einer
 *                Mischreise lösen weiterhin hierüber auf (Regel 10 — maßgeblicher Ort DE ⇒ 28/14).
 * @param options NEU (RK-14): additiv-optional. `foreignRates` aktiviert den Ausland-Zweig
 *                (§13.5 Regeln 10–13); weggelassen ⇒ byte-identisches MVP-Verhalten.
 * @returns `{ ok: true, … }` mit Tages-/Summen-Ergebnis oder `{ ok: false, error }`.
 */
export function calculateTravelAllowances(
  input: TravelCalcInput,
  cfg: TravelRatesConfig,
  options?: TravelCalcOptions
): TravelCalcResult {
  // ── Regel 9, Schritt 1: Datum/Zeit parsen + Range prüfen ──────────────────────
  const departureDayNumber = parseIsoDateToDayNumber(input.departureDateISO);
  const returnDayNumber = parseIsoDateToDayNumber(input.returnDateISO);
  const departureMinutes = parseHhMmToMinutes(input.departureTime);
  const returnMinutes = parseHhMmToMinutes(input.returnTime);
  if (
    departureDayNumber === null ||
    returnDayNumber === null ||
    departureMinutes === null ||
    returnMinutes === null
  ) {
    return fail('invalidDateRange');
  }
  const departureDateTimeMinutes = departureDayNumber * MINUTES_PER_DAY + departureMinutes;
  const returnDateTimeMinutes = returnDayNumber * MINUTES_PER_DAY + returnMinutes;
  if (returnDateTimeMinutes < departureDateTimeMinutes) {
    return fail('invalidDateRange');
  }

  // ── Regel 9, Schritt 2: days[] deckt Kalendertage lückenlos + eindeutig ab ────
  const numDays = returnDayNumber - departureDayNumber + 1;
  const expectedDates: string[] = [];
  for (let offset = 0; offset < numDays; offset += 1) {
    expectedDates.push(dayNumberToIso(departureDayNumber + offset));
  }
  if (input.days.length !== numDays) {
    return fail('daysMismatch');
  }
  // Sortier-Kopie (dateISO ist lexikografisch == chronologisch) für Vergleich + Tagestypen.
  const sortedDays = [...input.days].sort((a, b) => (a.dateISO < b.dateISO ? -1 : a.dateISO > b.dateISO ? 1 : 0));
  for (let index = 0; index < numDays; index += 1) {
    if (sortedDays[index].dateISO !== expectedDates[index]) {
      // Fängt Lücken, Duplikate UND ungültige Tages-dateISO-Werte (#13/#16).
      return fail('daysMismatch');
    }
  }

  // ── Regel 9, Schritt 3: km-Eingangswert auf Number.isFinite (Kern-Guard, Regel 8) ──
  if (input.mileage !== null && !Number.isFinite(input.mileage.km)) {
    return fail('nonFiniteInput');
  }

  // ── Regel 9, Schritt 4: erste Tätigkeitsstätte ────────────────────────────────
  if (input.isFirstWorkplace) {
    return fail('firstWorkplaceTrip');
  }

  // ── Regel 1: Reise-Kategorie + Übernachtungs-Gate ─────────────────────────────
  // eintägig: genau 1 Kalendertag.
  // Mitternachts-Sonderfall: genau 2 Kalendertage UND days[0].overnight === false ⇒ eintägige Logik.
  // sonst mehrtägig: 14/28/14 NUR bei lückenloser Übernachtungskette.
  const isSingleCalendarDay = numDays === 1;
  const isMidnightSingleDay = numDays === 2 && sortedDays[0].overnight === false;
  const isMultiDay = !isSingleCalendarDay && !isMidnightSingleDay;

  // ── Regel 9, Schritt 5: Übernachtungs-Gate für echte Mehrtagesreisen ──────────
  if (isMultiDay) {
    // overnight === true an ALLEN Tagen außer dem letzten; letzter Tag wird ignoriert.
    for (let index = 0; index < numDays - 1; index += 1) {
      if (sortedDays[index].overnight !== true) {
        return fail('missingOvernightForMultiDay');
      }
    }
  }

  // ── Regel 9, Schritt 6: Auslandstag (chronologisch erster) ────────────────────
  // Regel 10 (RK-14): Der fail-closed non-DE-Block gilt NUR, wenn das Sub-Flag zu ist
  // (kein `options.foreignRates`). Mit Auslands-Sätzen entfällt dieser Schritt — non-DE-Tage
  // werden in Schritt 7 über die Auslands-Kaskade aufgelöst (byte-identisches MVP-Verhalten
  // ohne foreignRates ⇒ Soll-Paar #40; mit foreignRates ⇒ Soll-Paare #28–#38).
  const foreignRates = options?.foreignRates;
  if (foreignRates === undefined) {
    for (let index = 0; index < numDays; index += 1) {
      if (sortedDays[index].countryCode !== 'DE') {
        return fail('foreignCountryNotSupported');
      }
    }
  }

  // ── Regel 9, Schritt 7: Sätze je Tag auflösen (chronologisch erster Null-Tag) ──
  // Pro Tag Verpflegungs-Satz (Inland ODER Ausland-Kaskade, Regel 10/11) + Kürzungs-Satz;
  // beide müssen non-null sein (fail-closed §E6). Die Mahlzeitenkürzung bleibt IMMER die
  // gesetzliche Inland-Kürzung (20/40/40 länderunabhängig, §9 Abs.4a S.8 EStG, Regel 11);
  // ihre BEZUGSGRÖSSE ist der volle Tagessatz DES TAGES-LANDES — den trägt der bestehende
  // `buildDayResult`-Pfad bereits, weil er per-Tag-`fullDayCents` (den aufgelösten) nimmt.
  const resolvedPerDiem: {
    fullDayCents: number;
    arrivalDepartureDayCents: number;
    rateSource?: ForeignRateSource;
  }[] = [];
  const resolvedReductions: { breakfastPct: number; lunchPct: number; dinnerPct: number }[] = [];
  for (let index = 0; index < numDays; index += 1) {
    const day = sortedDays[index];

    // Regel 10: non-DE-Tag + offenes Sub-Flag ⇒ Auslands-Kaskade; sonst Inland-Config
    // (auch DE-Tage einer Mischreise). Ohne foreignRates ist countryCode hier immer 'DE'
    // (Schritt 6 hätte sonst bereits fail-closed geblockt).
    let fullDayCents: number;
    let arrivalDepartureDayCents: number;
    let rateSource: ForeignRateSource | undefined;
    if (foreignRates !== undefined && day.countryCode !== 'DE') {
      // ── Regel 9, Schritt 7a (A-6): Zusicherungs-Horizont VOR der Kaskade ──────
      // Eigener, unterscheidbarer Code — und bewusst VOR dem Resolver: sonst käme ein Tag
      // jenseits des Horizonts als `noRatesForDate` zurück und wäre von „Land unbekannt"
      // nicht mehr zu trennen (Ausweg-Text und Ursache sind aber verschieden).
      if (!isForeignDateAssured(day.dateISO, foreignRates)) {
        return fail('foreignRatesNotYetAvailableForDate');
      }
      const foreignResolution = resolveForeignPerDiemRate(
        day.dateISO,
        day.countryCode,
        day.regionKey ?? null,
        foreignRates
      );
      if (foreignResolution === null) {
        return fail('noRatesForDate');
      }
      fullDayCents = foreignResolution.entry.fullDayCents;
      arrivalDepartureDayCents = foreignResolution.entry.arrivalDepartureDayCents;
      rateSource = foreignResolution.rateSource;
    } else {
      const perDiem = resolvePerDiemRate(day.dateISO, day.countryCode, cfg);
      if (perDiem === null) {
        return fail('noRatesForDate');
      }
      fullDayCents = perDiem.fullDayCents;
      arrivalDepartureDayCents = perDiem.arrivalDepartureDayCents;
    }

    const reduction = resolveMealReductions(day.dateISO, cfg);
    if (reduction === null) {
      return fail('noRatesForDate');
    }
    // Konfig-Cents-Finiteness (Regel 8, „alle Konfig-Cents") — bei sauberer Config unerreichbar.
    if (
      !Number.isFinite(fullDayCents) ||
      !Number.isFinite(arrivalDepartureDayCents) ||
      !Number.isFinite(reduction.breakfastPct) ||
      !Number.isFinite(reduction.lunchPct) ||
      !Number.isFinite(reduction.dinnerPct)
    ) {
      return fail('nonFiniteInput');
    }
    // rateSource NUR mitführen, wenn Auslands-Satz griff (LANDMINE: Key sonst WEGLASSEN).
    resolvedPerDiem.push(
      rateSource !== undefined
        ? { fullDayCents, arrivalDepartureDayCents, rateSource }
        : { fullDayCents, arrivalDepartureDayCents }
    );
    resolvedReductions.push({
      breakfastPct: reduction.breakfastPct,
      lunchPct: reduction.lunchPct,
      dinnerPct: reduction.dinnerPct,
    });
  }

  // Jahrgangs-Version für den Snapshot: NUR wenn ≥1 Tag über die Auslands-Sätze aufgelöst
  // wurde (§13.5/§13.9). Reine Inlandsreise (auch mit übergebenem foreignRates, #39) ⇒ Key weg.
  const anyForeignRateApplied = resolvedPerDiem.some((entry) => entry.rateSource !== undefined);
  const foreignRatesVersionId =
    anyForeignRateApplied && foreignRates !== undefined ? foreignRates.versionId : undefined;

  // ── Kilometergeld (Regel 5): Satz über returnDateISO auflösen ─────────────────
  let mileageCents = 0;
  if (input.mileage !== null) {
    const mileageRate = resolveMileageRate(input.returnDateISO, cfg);
    if (mileageRate === null) {
      return fail('noRatesForDate');
    }
    const centsPerKm =
      input.mileage.vehicle === 'car' ? mileageRate.carCentsPerKm : mileageRate.motorcycleCentsPerKm;
    if (!Number.isFinite(centsPerKm)) {
      return fail('nonFiniteInput');
    }
    mileageCents = Math.round(input.mileage.km * centsPerKm);
  }

  // ── Verpflegung berechnen ─────────────────────────────────────────────────────
  const warnings: TravelCalcWarningCode[] = [];
  let dayResults: TravelDayCalcResult[];

  if (isMultiDay) {
    dayResults = buildMultiDayResults(sortedDays, resolvedPerDiem, resolvedReductions, numDays);
  } else {
    // eintägig ODER Mitternachts-Sonderfall: eintägige >8h-Logik auf die Gesamt-Abwesenheit.
    const totalAbsenceMinutes = returnDateTimeMinutes - departureDateTimeMinutes;
    const qualifies = totalAbsenceMinutes > EIGHT_HOURS_MINUTES; // strikt > 8:00 h
    if (!qualifies) {
      warnings.push('shortSingleDayNoAllowance');
    }
    if (isMidnightSingleDay) {
      warnings.push('midnightSingleDaySplit');
      dayResults = buildMidnightResults(
        sortedDays,
        resolvedPerDiem,
        resolvedReductions,
        qualifies,
        departureMinutes,
        returnMinutes
      );
    } else {
      dayResults = buildSingleDayResults(sortedDays, resolvedPerDiem, resolvedReductions, qualifies);
    }
  }

  // 3-Monats-Frist wird im MVP NICHT geprüft — Warnung IMMER (V14, Transparenz).
  warnings.push('threeMonthRuleNotChecked');

  const verpflegungCents = dayResults.reduce((sum, day) => sum + day.allowanceCents, 0);
  const totalCents = verpflegungCents + mileageCents;

  // ── Regel 8: Abschluss-Guard (Ergebnis-Ebene) — jedes Cent-Feld Integer & finite ──
  if (!isFiniteIntegerResult(dayResults, verpflegungCents, mileageCents, totalCents)) {
    return fail('nonFiniteInput');
  }

  // foreignRatesVersionId NUR setzen, wenn Auslands-Sätze griffen (LANDMINE: Key sonst WEGLASSEN,
  // nie `undefined` materialisieren — hält #1–#27/#39 grün + `assertNoUndefinedValues`, V10).
  return {
    ok: true,
    days: dayResults,
    verpflegungCents,
    mileageCents,
    totalCents,
    warnings,
    ratesVersionId: cfg.versionId,
    ...(foreignRatesVersionId !== undefined ? { foreignRatesVersionId } : {}),
  };
}

// ─── Tages-Erzeugung (private) ─────────────────────────────────────────────────

/**
 * Mehrtagesreise: anreise / zwischentag / abreise, Kürzung vom vollen Satz. Sätze sind je Tag
 * bereits aufgelöst (Inland 1400/2800 ODER länderindividueller Auslands-Satz, Regel 11); der
 * per-Tag-`rateSource` wandert unverändert in das Tages-Ergebnis.
 */
function buildMultiDayResults(
  sortedDays: TravelDayRecord[],
  perDiem: { fullDayCents: number; arrivalDepartureDayCents: number; rateSource?: ForeignRateSource }[],
  reductions: { breakfastPct: number; lunchPct: number; dinnerPct: number }[],
  numDays: number
): TravelDayCalcResult[] {
  const results: TravelDayCalcResult[] = [];
  for (let index = 0; index < numDays; index += 1) {
    const day = sortedDays[index];
    const isFirst = index === 0;
    const isLast = index === numDays - 1;
    const dayType: TravelDayType = isFirst ? 'anreise' : isLast ? 'abreise' : 'zwischentag';
    // Basis: Zwischentag voller Satz, An-/Abreisetag reduzierter Satz OHNE 8h-Prüfung (Regel 2/11).
    const baseCents = dayType === 'zwischentag' ? perDiem[index].fullDayCents : perDiem[index].arrivalDepartureDayCents;
    results.push(
      buildDayResult(day, dayType, baseCents, perDiem[index].fullDayCents, reductions[index], perDiem[index].rateSource)
    );
  }
  return results;
}

/** Eintägige Reise (1 Kalendertag): reduzierter Satz nur bei Abwesenheit strikt > 8 h, sonst 0 €. */
function buildSingleDayResults(
  sortedDays: TravelDayRecord[],
  perDiem: { fullDayCents: number; arrivalDepartureDayCents: number; rateSource?: ForeignRateSource }[],
  reductions: { breakfastPct: number; lunchPct: number; dinnerPct: number }[],
  qualifies: boolean
): TravelDayCalcResult[] {
  const day = sortedDays[0];
  const baseCents = qualifies ? perDiem[0].arrivalDepartureDayCents : 0;
  return [buildDayResult(day, 'eintaegig', baseCents, perDiem[0].fullDayCents, reductions[0], perDiem[0].rateSource)];
}

/**
 * Mitternachts-Sonderfall (2 Kalendertage, keine Übernachtung, §9 Abs.4a S.3 Nr.3 Hs.2 EStG):
 * EIN eintägiger Betrag, zugeordnet dem Kalendertag mit überwiegender Abwesenheit (Tie → Tag 1).
 * Der zugeordnete Tag trägt Basis + die SUMME der Kürzungen BEIDER Tage (konservativ, nie zu
 * hoch); der andere Tag bleibt 0. dayType beider Tage = 'eintaegig'.
 */
function buildMidnightResults(
  sortedDays: TravelDayRecord[],
  perDiem: { fullDayCents: number; arrivalDepartureDayCents: number; rateSource?: ForeignRateSource }[],
  reductions: { breakfastPct: number; lunchPct: number; dinnerPct: number }[],
  qualifies: boolean,
  departureMinutes: number,
  returnMinutes: number
): TravelDayCalcResult[] {
  const day1AbsenceMinutes = MINUTES_PER_DAY - departureMinutes; // Abreisezeit → Mitternacht
  const day2AbsenceMinutes = returnMinutes; // Mitternacht → Rückkehrzeit
  const assignedIndex = day1AbsenceMinutes >= day2AbsenceMinutes ? 0 : 1; // Tie → erster Tag

  const baseCents = qualifies ? perDiem[assignedIndex].arrivalDepartureDayCents : 0;
  const fullDayCents = perDiem[assignedIndex].fullDayCents;

  // Kürzungen BEIDER Tage summieren und dem zugeordneten Tag geben (nie zu hoch abziehen).
  let breakfastCents = 0;
  let lunchCents = 0;
  let dinnerCents = 0;
  for (let index = 0; index < 2; index += 1) {
    breakfastCents += mealReductionCents(fullDayCents, reductions[index].breakfastPct, sortedDays[index].breakfastProvided);
    lunchCents += mealReductionCents(fullDayCents, reductions[index].lunchPct, sortedDays[index].lunchProvided);
    dinnerCents += mealReductionCents(fullDayCents, reductions[index].dinnerPct, sortedDays[index].dinnerProvided);
  }
  const allowanceCents = Math.max(0, baseCents - (breakfastCents + lunchCents + dinnerCents));

  // rateSource je Tag aus dessen eigener Satz-Auflösung (Regel 13: Ausland-Mitternachtsfall);
  // LANDMINE: Key nur mitführen, wenn der jeweilige Tag über einen Auslands-Satz aufgelöst wurde.
  return [0, 1].map((index) => {
    const rateSource = perDiem[index].rateSource;
    if (index === assignedIndex) {
      return {
        dateISO: sortedDays[index].dateISO,
        dayType: 'eintaegig' as const,
        countryCode: sortedDays[index].countryCode,
        baseCents,
        reductionBreakfastCents: breakfastCents,
        reductionLunchCents: lunchCents,
        reductionDinnerCents: dinnerCents,
        allowanceCents,
        ...(rateSource !== undefined ? { rateSource } : {}),
      };
    }
    return {
      dateISO: sortedDays[index].dateISO,
      dayType: 'eintaegig' as const,
      countryCode: sortedDays[index].countryCode,
      baseCents: 0,
      reductionBreakfastCents: 0,
      reductionLunchCents: 0,
      reductionDinnerCents: 0,
      allowanceCents: 0,
      ...(rateSource !== undefined ? { rateSource } : {}),
    };
  });
}

/**
 * Baut ein Tages-Ergebnis mit Mahlzeitenkürzung vom vollen Tagessatz + 0-Deckelung (Regel 3/11).
 * `fullDayCents` ist der volle Tagessatz DES TAGES-LANDES (Inland ODER Ausland) — dadurch ist die
 * prozentuale Auslandskürzung (20/40/40 vom Länder-Satz, Regel 11) ohne Sonderpfad korrekt.
 * `rateSource` wird NUR gesetzt, wenn ein Auslands-Satz griff (LANDMINE: Key sonst WEGLASSEN).
 */
function buildDayResult(
  day: TravelDayRecord,
  dayType: TravelDayType,
  baseCents: number,
  fullDayCents: number,
  reduction: { breakfastPct: number; lunchPct: number; dinnerPct: number },
  rateSource: ForeignRateSource | undefined
): TravelDayCalcResult {
  const reductionBreakfastCents = mealReductionCents(fullDayCents, reduction.breakfastPct, day.breakfastProvided);
  const reductionLunchCents = mealReductionCents(fullDayCents, reduction.lunchPct, day.lunchProvided);
  const reductionDinnerCents = mealReductionCents(fullDayCents, reduction.dinnerPct, day.dinnerProvided);
  const allowanceCents = Math.max(0, baseCents - (reductionBreakfastCents + reductionLunchCents + reductionDinnerCents));
  return {
    dateISO: day.dateISO,
    dayType,
    countryCode: day.countryCode,
    baseCents,
    reductionBreakfastCents,
    reductionLunchCents,
    reductionDinnerCents,
    allowanceCents,
    ...(rateSource !== undefined ? { rateSource } : {}),
  };
}

/** Abschluss-Guard (Regel 8): alle Ergebnis-Cent-Felder müssen finite Integer sein. */
function isFiniteIntegerResult(
  dayResults: TravelDayCalcResult[],
  verpflegungCents: number,
  mileageCents: number,
  totalCents: number
): boolean {
  const isInt = (value: number): boolean => Number.isFinite(value) && Number.isInteger(value);
  for (const day of dayResults) {
    if (
      !isInt(day.baseCents) ||
      !isInt(day.reductionBreakfastCents) ||
      !isInt(day.reductionLunchCents) ||
      !isInt(day.reductionDinnerCents) ||
      !isInt(day.allowanceCents)
    ) {
      return false;
    }
  }
  return isInt(verpflegungCents) && isInt(mileageCents) && isInt(totalCents);
}
